// The ≤880px body of TemplateModal (mobile overhaul §15 frame D): template
// name, the tasks as ranked rows with a drag handle, a quick-add row and the
// delete row. Tapping a row — or its handle without dragging — opens a half
// sheet with title, notes, priority, Move up / Move down and Remove, so the
// order can always be changed without a drag (Henry's decision 6).
//
// Lives outside pages/Settings.tsx so the desktop modal keeps its shape; the
// reorder maths is in lib/taskTemplateOrder.ts, where it is unit-tested.

import { useRef, useState, type Dispatch, type SetStateAction } from 'react';
import type { TaskTemplateItem } from '../pages/Settings';
import { NOTES_MAX_LENGTH } from '../../shared/taskNotes';
import { moveTaskTemplateItem, reorderTaskTemplateItems } from '../lib/taskTemplateOrder';
import { TrashIcon } from './icons';
import { Seg } from './primitives';
import { Sheet, SheetButton, SheetRow, SheetSection } from './Sheet';

export function TemplateMobileBody({ name, setName, items, setItems, busy, onDelete }: {
  name: string; setName: (name: string) => void;
  items: TaskTemplateItem[]; setItems: Dispatch<SetStateAction<TaskTemplateItem[]>>;
  busy: boolean; onDelete?: () => void;
}) {
  const [editing, setEditing] = useState<number | null>(null);
  const [dragging, setDragging] = useState<number | null>(null);
  const [target, setTarget] = useState<number | null>(null);
  const drag = useRef<{ from: number; to: number; startY: number; moved: boolean } | null>(null);
  const list = useRef<HTMLDivElement>(null);
  const item = editing === null ? null : items[editing];
  const patch = (change: Partial<TaskTemplateItem>) => setItems(current => current.map((it, i) => i === editing ? { ...it, ...change } : it));
  const move = (direction: 'up' | 'down') => {
    if (editing === null) return;
    setItems(current => moveTaskTemplateItem(current, editing, direction));
    setEditing(editing + (direction === 'up' ? -1 : 1));
  };
  return <div className="settings-template-body">
    <label className="f-field"><span className="f-label">Template name <span className="req">*</span></span>
      <input className="f-input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Shoot prep" disabled={busy} />
    </label>
    <SheetSection>Tasks — in order</SheetSection>
    <div ref={list}>
      {items.map((task, i) => <div key={i} data-template-index={i} className={`settings-template-row${dragging === i ? ' dragging' : ''}${target === i && dragging !== i ? ' drop-target' : ''}`}>
        <span className="settings-template-rank">{i + 1}</span>
        <button className="settings-template-task" disabled={busy} onClick={() => setEditing(i)}>
          <span>{task.title || 'Untitled task'}</span>
          {(task.notes || task.priority === 1) && <small>{task.notes ? 'notes' : ''}{task.notes && task.priority === 1 ? ' · ' : ''}{task.priority === 1 && <span className="gold">⚑ high</span>}</small>}
        </button>
        <button className="settings-template-handle" disabled={busy} aria-label={`Reorder ${task.title || `task ${i + 1}`}; tap for move controls`}
          onClick={() => { const moved = drag.current?.moved; drag.current = null; if (!moved) setEditing(i); }}
          onPointerDown={e => {
            if (e.button !== 0) return;
            e.currentTarget.setPointerCapture(e.pointerId);
            drag.current = { from: i, to: i, startY: e.clientY, moved: false };
            setDragging(i); setTarget(i);
          }}
          onPointerMove={e => {
            const active = drag.current;
            if (!active || !e.currentTarget.hasPointerCapture(e.pointerId)) return;
            if (Math.abs(e.clientY - active.startY) < 6 && !active.moved) return;
            active.moved = true;
            const rows = Array.from(list.current?.querySelectorAll<HTMLElement>('[data-template-index]') ?? []);
            const nearest = rows.reduce((best, row, idx) => {
              const rect = row.getBoundingClientRect();
              const distance = Math.abs(e.clientY - (rect.top + rect.height / 2));
              return distance < best.distance ? { index: idx, distance } : best;
            }, { index: active.from, distance: Infinity });
            active.to = nearest.index; setTarget(nearest.index);
          }}
          onPointerUp={e => {
            const active = drag.current;
            if (!active) return;
            if (active.moved) setItems(current => reorderTaskTemplateItems(current, active.from, active.to));
            if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
            setDragging(null); setTarget(null);
          }}
          onPointerCancel={() => { drag.current = null; setDragging(null); setTarget(null); }}
        >≡</button>
      </div>)}
    </div>
    <SheetRow icon="+" label="Add task…" disabled={busy} onSelect={() => {
      setEditing(items.length);
      setItems(current => [...current, { title: '', notes: null, priority: 2 }]);
    }} />
    {onDelete && <SheetRow icon={<TrashIcon />} label="Delete template" tone="danger" disabled={busy} onSelect={onDelete} />}
    {item && editing !== null && <Sheet open variant="half" title="Edit task" className="settings-template-edit" onClose={() => setEditing(null)}
      rightAction={<SheetButton onClick={() => setEditing(null)} tone="accent">Done</SheetButton>}>
      <div className="settings-template-fields">
        <label className="f-field"><span className="f-label">Title</span><input className="f-input" value={item.title} onChange={e => patch({ title: e.target.value })} /></label>
        <label className="f-field"><span className="f-label">Notes</span><textarea className="f-input" rows={3} maxLength={NOTES_MAX_LENGTH} value={item.notes ?? ''} onChange={e => patch({ notes: e.target.value || null })} /></label>
        <div className="f-field"><span className="f-label">Priority</span><Seg value={String(item.priority)} options={[{ value: '2', label: 'Normal' }, { value: '1', label: 'High' }]} onChange={value => patch({ priority: Number(value) })} /></div>
      </div>
      <SheetRow label="Move up" disabled={editing === 0} onSelect={() => move('up')} />
      <SheetRow label="Move down" disabled={editing === items.length - 1} onSelect={() => move('down')} />
      <SheetRow label="Remove task" tone="danger" onSelect={() => { setItems(current => current.filter((_, i) => i !== editing)); setEditing(null); }} />
    </Sheet>}
  </div>;
}
